import { useEffect, useMemo, useState } from "react";
import { Search, Info, ExternalLink, Mic, BookOpen, Boxes, Users, Printer } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FAMILY_COLOR = {
  Temporaire: "bg-blue-100 text-blue-700",
  Permanent: "bg-[#C89A3E]/20 text-[#8A5A1E]",
  Conjointe: "bg-purple-100 text-purple-700",
  Entreprise: "bg-slate-200 text-slate-700",
  Avenant: "bg-emerald-100 text-emerald-700",
  Collective: "bg-rose-100 text-rose-700",
  Placement: "bg-cyan-100 text-cyan-700",
  Rente: "bg-indigo-100 text-indigo-700",
  "Invalidité": "bg-orange-100 text-orange-700",
  "Maladie/Soins": "bg-pink-100 text-pink-700",
  "Santé": "bg-teal-100 text-teal-700",
};

export const ProductCases = ({ onTrain }) => {
  const [data, setData] = useState(null);
  const [modules, setModules] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("cas");
  const [moduleFilter, setModuleFilter] = useState("");

  useEffect(() => {
    api.productCases().then(setData).catch(() => {});
    api.modules().then(setModules).catch(() => {});
  }, []);

  const prodById = useMemo(() => Object.fromEntries((data?.products || []).map((p) => [p.id, p])), [data]);
  const modBySlug = useMemo(() => Object.fromEntries(modules.map((m) => [m.slug, m])), [modules]);

  const openModule = (slug) => window.open(`/formations/${slug}`, "_blank", "noopener");

  const [printing, setPrinting] = useState(null);
  const printFiche = async (id) => {
    setPrinting(id);
    try {
      const blob = await api.caseFichePdf(id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Impossible de générer la fiche PDF.");
    } finally {
      setPrinting(null);
    }
  };

  const cases = useMemo(() => data?.cases || [], [data]);
  const usedSlugs = useMemo(() => {
    const s = new Set();
    cases.forEach((c) => (c.module_slugs || []).forEach((sl) => s.add(sl)));
    return [...s];
  }, [cases]);

  const filteredCases = useMemo(() => {
    const term = q.trim().toLowerCase();
    return cases.filter((c) => {
      if (moduleFilter && !(c.module_slugs || []).includes(moduleFilter)) return false;
      if (!term) return true;
      const hay = [c.titre, c.profil, c.besoin, (c.objections || []).join(" "),
        (c.produits || []).map((id) => prodById[id]?.name).join(" ")].join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [cases, q, moduleFilter, prodById]);

  const filteredProducts = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.products || []).filter((p) => {
      if (moduleFilter && !(p.module_slugs || []).includes(moduleFilter)) return false;
      if (!term) return true;
      return [p.name, p.description, p.pour_qui, p.famille].join(" ").toLowerCase().includes(term);
    });
  }, [data, q, moduleFilter]);

  const ModuleLinks = ({ slugs }) => (
    <div className="flex flex-wrap gap-1.5">
      {(slugs || []).map((sl) => modBySlug[sl] ? (
        <button key={sl} data-testid={`case-module-${sl}`} onClick={() => openModule(sl)}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#012141] bg-[#012141]/6 hover:bg-[#C89A3E]/20 px-2 py-1 rounded-full transition-colors">
          <BookOpen size={11} /> {modBySlug[sl].title} <ExternalLink size={10} />
        </button>
      ) : null)}
    </div>
  );

  if (!data) return <div className="text-slate-400 text-sm py-10 text-center">Chargement des produits et cas…</div>;

  return (
    <div data-testid="product-cases">
      <div className="flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4">
        <Info size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-800">{data.disclaimer}</p>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button data-testid="pc-tab-cas" onClick={() => setTab("cas")}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${tab === "cas" ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
          <Users size={15} /> Cas types <span className="opacity-60">({cases.length})</span>
        </button>
        <button data-testid="pc-tab-produits" onClick={() => setTab("produits")}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${tab === "produits" ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>
          <Boxes size={15} /> Types de produits <span className="opacity-60">({data.products.length})</span>
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="pc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un cas, un besoin, un produit…"
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl border border-[#012141]/15 bg-white text-sm focus:outline-none focus:border-[#C89A3E]" />
        </div>
        <select data-testid="pc-module-filter" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}
          className="px-3 py-2.5 rounded-2xl border border-[#012141]/15 bg-white text-sm text-[#012141] focus:outline-none focus:border-[#C89A3E]">
          <option value="">Tous les modules</option>
          {usedSlugs.filter((s) => modBySlug[s]).map((s) => <option key={s} value={s}>{modBySlug[s].title}</option>)}
        </select>
      </div>

      {tab === "cas" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filteredCases.map((c) => (
            <div key={c.id} data-testid={`case-card-${c.id}`} className="rounded-2xl border border-[#012141]/12 bg-white p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-heading font-bold text-[#012141]">{c.titre}</h4>
                  <span className="text-[11px] text-slate-400">{c.ref}</span>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#012141]/8 text-[#012141] whitespace-nowrap">Source : manuel AMF</span>
              </div>
              <p className="text-sm text-slate-600 mt-2"><b className="text-[#012141]">Profil :</b> {c.profil}</p>
              <p className="text-sm text-slate-600 mt-1.5"><b className="text-[#012141]">Besoin :</b> {c.besoin}</p>
              {c.produits?.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {c.produits.map((id) => (
                    <span key={id} className={`text-[11px] font-semibold px-2 py-1 rounded-full ${FAMILY_COLOR[prodById[id]?.famille] || "bg-slate-100 text-slate-600"}`}>
                      {prodById[id]?.name || id}
                    </span>
                  ))}
                </div>
              )}
              {c.montants && <p className="text-xs text-slate-500 mt-2"><b>Montants :</b> {c.montants}</p>}
              {c.arguments?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {c.arguments.map((a, i) => <li key={i} className="text-xs text-slate-600 flex gap-1.5"><span className="text-[#C89A3E]">◆</span>{a}</li>)}
                </ul>
              )}
              {c.objections?.length > 0 && (
                <div className="mt-2.5 rounded-xl bg-[#FDFDFD] border border-[#012141]/8 p-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Objections fréquentes</div>
                  <ul className="space-y-0.5">{c.objections.map((o, i) => <li key={i} className="text-xs text-slate-600 italic">{o}</li>)}</ul>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-[#012141]/8">
                <ModuleLinks slugs={c.module_slugs} />
              </div>
              <div className="mt-3 flex gap-2">
                <button data-testid={`case-print-${c.id}`} onClick={() => printFiche(c.id)} disabled={printing === c.id}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full border border-[#012141]/20 text-[#012141] font-semibold text-sm hover:border-[#C89A3E] disabled:opacity-50">
                  <Printer size={15} /> {printing === c.id ? "…" : "Fiche PDF"}
                </button>
                <button data-testid={`case-train-${c.id}`} onClick={() => onTrain?.(c)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-[#C89A3E] text-[#012141] font-bold text-sm hover:brightness-105 transition-all">
                  <Mic size={15} /> S'entraîner sur ce cas
                </button>
              </div>
            </div>
          ))}
          {filteredCases.length === 0 && <p className="text-slate-400 text-sm py-8 text-center col-span-2">Aucun cas ne correspond à votre recherche.</p>}
        </div>
      )}

      {tab === "produits" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filteredProducts.map((p) => (
            <div key={p.id} data-testid={`product-card-${p.id}`} className="rounded-2xl border border-[#012141]/12 bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-heading font-bold text-[#012141]">{p.name}</h4>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${FAMILY_COLOR[p.famille] || "bg-slate-100 text-slate-600"}`}>{p.famille}</span>
              </div>
              <p className="text-sm text-slate-600 mt-2">{p.description}</p>
              <p className="text-sm text-slate-700 mt-1.5"><b className="text-[#012141]">À qui ça convient :</b> {p.pour_qui}</p>
              <div className="mt-3 pt-3 border-t border-[#012141]/8"><ModuleLinks slugs={p.module_slugs} /></div>
            </div>
          ))}
          {filteredProducts.length === 0 && <p className="text-slate-400 text-sm py-8 text-center col-span-2">Aucun produit ne correspond.</p>}
        </div>
      )}
    </div>
  );
};
